import React from 'react';
import { Square, RefreshCw, MessageSquare, GitPullRequest, ExternalLink, ArrowRight, Check } from 'lucide-react';
import { Fix } from '../../types/fix';
import { Spinner } from '../../design-system';
import { prTerm, prTermLong, providerLabel } from '../../config/providers';

export interface FixActionToolbarProps {
  fix: Fix;
  hasDiff: boolean;
  isReadOnly: boolean;
  isDemoOrg: boolean;
  /** Provider credentials (ADO PAT / AWS keys or role) are invalid or expired; retry & regenerate are blocked. */
  isCredentialsBlocked: boolean;
  isCancelling: boolean;
  isRetrying: boolean;
  isFixRegenerating: boolean;
  isFixRefining: boolean;
  isCreatingPR: boolean;
  isPRPending: boolean;
  canCancel: boolean;
  canRegenerate: boolean;
  canRefine: boolean;
  isFreeTierNoPRs: boolean;
  prQuotaExhausted: boolean;
  prStatusText: string | null;
  onCancel: () => void;
  onRetry: () => void;
  onRegenerate: () => void;
  onRefine: () => void;
  onCreatePR: () => void;
  onUpgrade: () => void;
  onBack: () => void;
  /** Read as "Back to {label}" — where the reader came from. */
  backLabel: string;
}

const PRIMARY_BTN =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-brand-primary hover:bg-brand-primaryDark text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const SECONDARY_BTN =
  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-ui-border-primary text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const DANGER_BTN =
  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const STATUS_TEXT = 'flex-1 min-w-[12rem] text-sm inline-flex items-center gap-2';

/**
 * Bottom action toolbar for the Fix detail page. Pinned to the bottom of the page so
 * it stays in view while the fix instructions + agent activity scroll above it. The
 * row it renders is driven entirely by the fix lifecycle (mirrors the mockup's
 * running / ready / failed / creating / PR-open / merged states).
 */
const FixActionToolbar: React.FC<FixActionToolbarProps> = ({
  fix,
  hasDiff,
  isReadOnly,
  isDemoOrg,
  isCredentialsBlocked,
  isCancelling,
  isRetrying,
  isFixRegenerating,
  isFixRefining,
  isCreatingPR,
  isPRPending,
  canCancel,
  canRegenerate,
  canRefine,
  isFreeTierNoPRs,
  prQuotaExhausted,
  prStatusText,
  onCancel,
  onRetry,
  onRegenerate,
  onRefine,
  onCreatePR,
  onUpgrade,
  onBack,
  backLabel,
}) => {
  const pr = fix.pull_requests && fix.pull_requests.length > 0 ? fix.pull_requests[0] : null;
  const providerName = providerLabel(fix.source_repo_provider);
  const prShort = prTerm(fix.source_repo_provider);
  const prLong = prTermLong(fix.source_repo_provider);
  const prMerged = pr?.merge_status === 'merged';
  const running = fix.status === 'pending' || fix.status === 'running';
  const failed = fix.status === 'failed' || fix.status === 'cancelled';
  const completedFamily =
    fix.status === 'completed' || fix.status === 'pr_creation_failed' || fix.status === 'pr_update_failed';

  const regenerateButton = (canRegenerate || fix.regeneration_pending) && (
    <button
      onClick={onRegenerate}
      disabled={!canRegenerate || isFixRegenerating || isFixRefining || isCredentialsBlocked || isReadOnly || isCreatingPR || isPRPending}
      className={SECONDARY_BTN}
    >
      {isFixRegenerating ? <Spinner size={16} tone="current" /> : <RefreshCw className="w-4 h-4" />}
      {isFixRegenerating ? 'Regenerating…' : 'Regenerate'}
    </button>
  );

  const refineButton = (canRefine || fix.refinement_pending) && (
    <button
      onClick={onRefine}
      disabled={!canRefine || isFixRefining || isFixRegenerating || isCredentialsBlocked || isReadOnly || isCreatingPR || isPRPending}
      className={SECONDARY_BTN}
    >
      {isFixRefining ? <Spinner size={16} tone="current" /> : <MessageSquare className="w-4 h-4" />}
      {isFixRefining ? 'Refining…' : 'Refine'}
    </button>
  );

  return (
    <div className="shrink-0 mt-6 rounded-lg border border-ui-border-primary bg-ui-background-secondary px-4 sm:px-6 py-3">
      {/* Reserve space on the right so action buttons never sit under the
          Chatwoot launcher pinned to the viewport's bottom-right corner. */}
      <div className="flex items-center gap-3 flex-wrap pr-16">
        {running && (
          <>
            <span className={`${STATUS_TEXT} text-ui-text-secondary`}>
              <Spinner size={16} className="shrink-0" />
              {fix.refinement_pending
                ? 'Refining the fix…'
                : fix.regeneration_pending
                ? 'Regenerating the fix…'
                : 'The Kolega agent is investigating the codebase and writing the fix…'}
            </span>
            {canCancel && (
              <button onClick={onCancel} disabled={isCancelling} className={DANGER_BTN}>
                {isCancelling ? <Spinner size={16} tone="current" /> : <Square className="w-4 h-4" />}
                {isCancelling ? 'Stopping…' : 'Stop'}
              </button>
            )}
          </>
        )}

        {failed && (
          <>
            <span className={`${STATUS_TEXT} text-semantic-error`}>
              {fix.status === 'cancelled' ? 'This fix run was stopped.' : 'This fix run failed.'}
            </span>
            <button onClick={onRetry} disabled={isRetrying || isCredentialsBlocked || isReadOnly} className={PRIMARY_BTN}>
              {isRetrying ? <Spinner size={16} tone="current" /> : <RefreshCw className="w-4 h-4" />}
              {isRetrying ? 'Retrying…' : 'Retry'}
            </button>
          </>
        )}

        {completedFamily && pr && prMerged && (
          <>
            <span className={`${STATUS_TEXT} text-semantic-success font-medium`}>
              <Check className="w-4 h-4 shrink-0" />
              PR #{pr.pr_number} merged · finding auto-resolved.
            </span>
            <button onClick={onBack} className={SECONDARY_BTN}>
              Back to {backLabel}
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {completedFamily && pr && !prMerged && (
          <>
            <div className="flex-1 min-w-[12rem]">
              <div className="text-sm font-medium text-purple-400 inline-flex items-center gap-2">
                <GitPullRequest className="w-4 h-4 shrink-0" />
                PR #{pr.pr_number} · {prStatusText || 'open'}
              </div>
              <div className="text-xs text-ui-text-tertiary mt-0.5">
                When this PR is merged, Kolega auto-resolves the finding.
              </div>
            </div>
            {fix.pr_update_pending && (
              <span className="inline-flex items-center gap-2 text-sm text-ui-text-tertiary">
                <Spinner size={16} />
                Updating {prShort}…
              </span>
            )}
            {regenerateButton}
            {refineButton}
            <a href={pr.pr_url} target="_blank" rel="noopener noreferrer" className={SECONDARY_BTN}>
              Open {prShort} in {providerName}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </>
        )}

        {completedFamily && !pr && (
          <>
            {isCreatingPR ? (
              <span className={`${STATUS_TEXT} text-ui-text-secondary`}>
                <Spinner size={16} className="shrink-0" />
                Queueing {prLong}…
              </span>
            ) : isPRPending ? (
              <span className={`${STATUS_TEXT} text-ui-text-secondary`}>
                <Spinner size={16} className="shrink-0" />
                Opening {prLong} on {providerName}…
              </span>
            ) : (
              <>
                <span className={`${STATUS_TEXT} ${hasDiff ? 'text-semantic-success font-medium' : 'text-ui-text-secondary'}`}>
                  {hasDiff ? (
                    <>
                      <Check className="w-4 h-4 shrink-0" />
                      Fix ready — review the changes, then open a {prLong}.
                    </>
                  ) : (
                    'Fix complete.'
                  )}
                </span>
                {regenerateButton}
                {refineButton}
                {hasDiff &&
                  (isFreeTierNoPRs ? (
                    <button onClick={onUpgrade} className={PRIMARY_BTN}>
                      <GitPullRequest className="w-4 h-4" />
                      Upgrade to create {prShort}s
                    </button>
                  ) : prQuotaExhausted ? (
                    <button onClick={onUpgrade} className={SECONDARY_BTN}>
                      <GitPullRequest className="w-4 h-4" />
                      {prShort} quota exhausted
                    </button>
                  ) : (
                    <button
                      onClick={onCreatePR}
                      disabled={isReadOnly || isDemoOrg}
                      title={isDemoOrg ? 'Demo repositories are public and read-only' : undefined}
                      className={PRIMARY_BTN}
                    >
                      <GitPullRequest className="w-4 h-4" />
                      {isDemoOrg ? `${prShort}s unavailable in demo` : fix.status === 'pr_creation_failed' ? `Retry ${prShort}` : `Create ${prShort}`}
                      {!isDemoOrg && <ArrowRight className="w-4 h-4" />}
                    </button>
                  ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FixActionToolbar;
